/* eslint-disable react/no-multi-comp */
import React, { useCallback, useEffect, useMemo } from 'react';

import { get, isEmpty, reverse, sortBy } from 'lodash';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { ApproveAdvancementsButton } from '@progress/components';
import { advancementStatuses, navigateToPurchaseOrderDetails } from '@shared';
import { Fab, FilterButton, TableMobile } from '@shared/components';
import { hasPermissionSel, permissions } from '@user';
import { toggleArrayItem } from '@utils';

import { setFromActivityLogs } from '../../../../../progress/approveAdvancements';
import { advancementTypesFilterSel, sorterSel } from '../../../duck/selectors';
import PendingItemsFilterCard from '../PendingItemsFilterCard';
import rows from './PendingItemsListRow';
import styles from './PendingItemsTable.less';

const { StickyTitle } = TableMobile;

const PendingItemsList = ({
  items,
  loading,
  selectedRowKeys,
  isOffline,
  onSetSelectedRowKeys,
  onGoToYouthProfile,
  onGoToAdultProfile,
  showPOID,
  showUpdate = true,
  isAwarding = false,
}) => {
  const dispatch = useDispatch();
  const advancementTypesFilter = useSelector(advancementTypesFilterSel);
  const sorter = useSelector(sorterSel);
  const canApprove = useSelector(state =>
    hasPermissionSel(state, permissions.APPROVE_ADVANCEMENTS),
  );
  const areAnyAdvancementsSelected = selectedRowKeys.length > 0;
  const selectedAdvancements = useMemo(
    () => items.filter(({ key }) => selectedRowKeys.includes(key)),
    [items, selectedRowKeys],
  );

  const transformedItems = useMemo(() => {
    let filtered = items;

    const advancementFilter = Object.keys(advancementTypesFilter).filter(
      key => advancementTypesFilter[key] === true,
    );

    if (!isEmpty(advancementFilter)) {
      filtered = filtered.filter(({ advancementType }) =>
        advancementFilter.includes(advancementType),
      );
    }

    if (sorter.columnKey === 'name') {
      filtered = sortBy(filtered, 'name');
    }

    if (sorter.columnKey === 'completionDate') {
      filtered = sortBy(filtered, 'date');
    }

    if (sorter.columnKey === 'description') {
      filtered = sortBy(filtered, 'description');
    }

    if (sorter.columnKey === 'advancementItem') {
      filtered = sortBy(filtered, 'advancementType');
    }

    if (get(sorter, 'order') === 'descend') {
      filtered = reverse(filtered);
    }

    return filtered;
  }, [items, sorter, advancementTypesFilter]);

  const handleSelectRow = useCallback(
    record => {
      if (!showUpdate) return;

      const newSelectedKeys = toggleArrayItem(selectedRowKeys, record.key);
      onSetSelectedRowKeys(newSelectedKeys);
    },
    [selectedRowKeys, onSetSelectedRowKeys, showUpdate],
  );

  const handleGoToPODetails = useCallback(
    poid => {
      dispatch(navigateToPurchaseOrderDetails(poid));
    },
    [dispatch],
  );

  const onGoToPODetails = showPOID ? handleGoToPODetails : false;

  useEffect(() => {
    dispatch(setFromActivityLogs(false));
  }, [dispatch]);

  return (
    <>
      <StickyTitle className={styles.stickyTitle} key="header">
        <PendingItemsFilterCard hideActivities>
          <FilterButton />
        </PendingItemsFilterCard>
      </StickyTitle>
      <TableMobile
        items={transformedItems}
        loading={loading}
        rows={rows({
          onGoToYouthProfile,
          onGoToAdultProfile,
          onGoToPODetails,
          showGoToPODetails: true,
        })}
        rowId="key"
        selectedKeys={selectedRowKeys}
        onItemClick={handleSelectRow}
      />
      <Fab visible={areAnyAdvancementsSelected && canApprove && showUpdate}>
        <ApproveAdvancementsButton
          className={styles.approveAdvancements}
          disabled={isOffline || !areAnyAdvancementsSelected}
          personsAdvancements={selectedAdvancements}
          advancementStatus={isAwarding ? advancementStatuses.AWARDED : null}
        />
      </Fab>
    </>
  );
};

PendingItemsList.propTypes = {
  items: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  sorter: PropTypes.object.isRequired,
  isOffline: PropTypes.bool.isRequired,
  showFilter: PropTypes.bool,
  selectedRowKeys: PropTypes.array,
  onSetSelectedRowKeys: PropTypes.func,
  onGoToYouthProfile: PropTypes.func,
  onGoToAdultProfile: PropTypes.func,
  onSorterChange: PropTypes.func,
  showPOID: PropTypes.bool,
  showUpdate: PropTypes.bool,
  isAwarding: PropTypes.bool,
};

export default PendingItemsList;
